import { Address } from "@/Common/Api/Contacts/Dto/Address";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { DateHelper } from "@/Common/Helpers/DateHelper";
import { SearchableItemModelT } from "@/Common/Models/SearchableItem";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import { CanadaState } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { BaseBritishColumbiaObstetricalFileSubSection, BaseBritishColumbiaObstetricalFileSubSectionProps } from "@/Patient/Components/MedicalNote/Obstetric/Form/BritishColumbia/Subcomponent/BaseBritishColumbiaObstetricalFileSubSection";
import { IndigenousIdentityStatus, ReserveLivingStatus } from "@/Patient/Models/ObstetricalFile/BritishColumbia/Part1/BritishColumbiaObstetricalFileIndigenousIdentity";

class BritishColumbiaObstetricalFileSection1 extends BaseBritishColumbiaObstetricalFileSubSection<BaseBritishColumbiaObstetricalFileSubSectionProps, never> {
    public render(): JSX.Element {
        return (
            <div className="section border border-bottom bc-antenatal-section-part1">
                {this.renderRow1()}
                {this.renderRow2()}
                {this.renderRow3()}
                {this.renderRow4()}
                {this.renderRow5()}
                {this.renderRow6()}
            </div>
        );
    }

    private renderRow1(): JSX.Element {
        const section1 = this.props.form.Section1;
        return (
            <div className="row row-form section-row">
                {this.renderField({
                    label: "ObstetricalFile_PrimaryMaternityCareProviderNameLabel",
                    field: this.controlRenderer.renderPhysicianSearchTextbox(
                        section1.PrimaryMaternityCareProviderName,
                        (attendant, isSelected) => this.props.mutateModelState(
                            form => form.Section1.PrimaryMaternityCareProviderName = attendant,
                            () => isSelected ? this.props.handleFormUpdate() : null
                        ),
                        null,
                        "ObstetricalFile_PrimaryMaternityCareProviderNameLabel",
                        this.page1FieldLimits.PrimaryMaternityCareProviderName.MaxChars
                    ),
                    className: "section-column-1",
                    labelPrefix: this.getTitleNumberAsLabelPrefix(1),
                    useLabelHtmlTagAsContainer: false
                })}
                {this.renderField({
                    label: "ObstetricalFile_FamilyPhysicianOrNursePractitionerNameLabel",
                    field: this.controlRenderer.renderTextbox({
                        value: section1.FamilyPhysicianOrNursePractitionerName,
                        onChange: text => this.props.mutateModelState(s => s.Section1.FamilyPhysicianOrNursePractitionerName = text),
                        maxLength: this.page1FieldLimits.FamilyPhysicianOrNursePractitionerName.MaxChars
                    }),
                    className: "section-column-1"
                })}
            </div>
        );
    }

    private renderRow2(): JSX.Element {
        const section1 = this.props.form.Section1;
        const patient = this.props.patient;
        return (
            <div className="row row-form section-row">
                <div className="section-column-1">
                    <div className="row">
                        {this.renderRedirectField(
                            "PatientSurnameLabel",
                            patient.IndividualResponse.LastName,
                            () => sessionStore.dispatch(LocalPatientOperations.Profile.PatientLastNameLabelClick),
                            "col-md-6"
                        )}
                        {this.renderRedirectField(
                            "PatientGivenNamesLabel",
                            patient.IndividualResponse.FirstName,
                            () => sessionStore.dispatch(LocalPatientOperations.Profile.PatientFirstNameLabelClick),
                            "col-md-6"
                        )}
                    </div>
                </div>
                <div className="section-column-1">
                    <div className="row">
                        {this.renderRedirectField(
                            "ObstetricalFile_DateofBirthLabel",
                            DateHelper.formatIsoDateOnly(patient.IndividualResponse.DateOfBirth),
                            () => sessionStore.dispatch(LocalPatientOperations.Profile.DOBLabelClick),
                            "col-md-6"
                        )}
                        {this.renderField({
                            label: "ObstetricalFile_AgeAtEstimatedDateOfDeliveryLabel",
                            field: this.controlRenderer.renderTextbox({
                                value: section1.AgeAtEstimatedDateOfDelivery?.toString() ?? null,
                                isNumeric: true,
                                onChange: value => this.props.mutateModelState(s => s.Section1.AgeAtEstimatedDateOfDelivery = value ? parseFloat(value) : null),
                                maxLength: this.page1FieldLimits.AgeAtEstimatedDateOfDelivery.MaxChars
                            }),
                            className: "col-md-6"
                        })}
                    </div>
                </div>
            </div>
        );
    }

    private renderRow3(): JSX.Element {
        const section1 = this.props.form.Section1;
        return (
            <div className="row row-form section-row">
                <div className="section-column-1">
                    <div className="row">
                        {this.renderField({
                            label: "SurnameAtBirthLabel",
                            field: this.controlRenderer.renderTextbox({
                                value: section1.SurnameAtBirth,
                                onChange: value => this.props.mutateModelState(s => s.Section1.SurnameAtBirth = value),
                                maxLength: this.page1FieldLimits.SurnameAtBirth.MaxChars
                            }),
                            className: "col-md-6"
                        })}
                        {this.renderField({
                            label: "PreferredNamePronounLabel",
                            field: this.controlRenderer.renderTextbox({
                                value: section1.PreferredNamePronoun,
                                onChange: value => this.props.mutateModelState(s => s.Section1.PreferredNamePronoun = value),
                                maxLength: this.page1FieldLimits.PreferredNameOrPronoun.MaxChars
                            }),
                            className: "col-md-6"
                        })}
                    </div>
                </div>
                <div className="section-column-1">
                    <div className="row">
                        {this.renderField({
                            label: "LanguagePreferredLabel",
                            field: this.controlRenderer.renderTextbox({
                                value: section1.PreferredLanguage,
                                onChange: value => this.props.mutateModelState(s => s.Section1.PreferredLanguage = value),
                                maxLength: this.page1FieldLimits.PreferredLanguage.MaxChars
                            }),
                            className: "col-md-6"
                        })}
                        {this.renderField({
                            label: "ObstetricalFile_RelationshipStatusLabel",
                            field: this.controlRenderer.renderAutoCompleteTextbox({
                                value: section1.RelationshipStatus,
                                autoCompleteItems: cacheStore.GetMaritalStatusLookups().map(p => p.ToSearchableItemModel(this.locale)),
                                onChange: status => this.props.mutateModelState(s => s.Section1.RelationshipStatus = status),
                                onAutoCompleteSelected: (code, model, value) => this.props.mutateModelStateWithFormUpdate(s => s.Section1.RelationshipStatus = value),
                                maxLength: this.page1FieldLimits.RelationshipStatus.MaxChars
                            }),
                            className: "col-md-6"
                        })}
                    </div>
                </div>
            </div>
        );
    }

    private renderRow4(): JSX.Element {
        const section1 = this.props.form.Section1;

        return (
            <div className="row">
                <div className="col-md-12">
                    <div className="row row-form section-row">
                        {this.renderField({
                            label: "ObstetricalFile_HighestLevelOfEducationCompletedLabel",
                            field: this.controlRenderer.renderAutoCompleteTextbox({
                                value: section1.HighestLevelEducationCompleted,
                                autoCompleteItems: this.getEducationLevelOptions(),
                                onChange: educationLevel => this.props.mutateModelState(s => s.Section1.HighestLevelEducationCompleted = educationLevel),
                                onAutoCompleteSelected: (code, _, value) => this.props.mutateModelStateWithFormUpdate(s => s.Section1.HighestLevelEducationCompleted = value),
                                maxLength: this.page1FieldLimits.HighestLevelOfEducationCompleted.MaxChars,
                                disableMatchAutoSorting: true
                            }),
                            className: "section-column-1"
                        })}
                        {this.renderField({
                            label: "OccupationLabel",
                            field: this.controlRenderer.renderTextbox({
                                value: section1.Occupation,
                                onChange: value => this.props.mutateModelState(s => s.Section1.Occupation = value),
                                maxLength: this.page1FieldLimits.PatientOccupation.MaxChars
                            }),
                            className: "section-column-1"
                        })}
                        <div className="section-column-1 flex-grow-1">
                            {this.renderRedirectField(
                                "lblAddress",
                                Address.GetFullAddress(this.props.patient.Addresses.firstPreferredOrDefault(p => p.IsPreferred)),
                                () => sessionStore.dispatch(LocalPatientOperations.Profile.AddressLabelClick)
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderRow5(): JSX.Element {
        const preferredPhoneNumber = PhoneNumber.GetPrimaryPhoneNumber(this.props.patient?.Phones);
        return (
            <div className="row row-form section-row">
                {this.renderIndigenousIdentitySection()}
                <div className="section-column-1 flex-grow-1">
                    <div className="row">
                        {this.renderRedirectField(
                            "PhoneNumberAltLabel",
                            preferredPhoneNumber ? PhoneNumber.stringFormattedPhone(preferredPhoneNumber) : "",
                            () => sessionStore.dispatch(
                                LocalPatientOperations.Profile.PhoneLabelClick,
                                { contactPointId: preferredPhoneNumber?.ContactPoint_id }
                            ),
                            "col-md-6"
                        )}
                        {this.renderRedirectField(
                            "PersonalHealthNumberLabel",
                            this.props.patient.HealthCards.firstOrDefault(p => p.ProvinceLookupId === cacheStore.GetLookupIdForProvince(CanadaState.BritishColumbia))?.Number ?? "",
                            () => sessionStore.dispatch(LocalPatientOperations.Profile.NAMLabelClick),
                            "col-md-6"
                        )}
                    </div>
                </div>
            </div>
        );
    }

    private renderRow6(): JSX.Element {
        const partnerInfo = this.props.form.Section1.PartnerInformation;
        return (
            <div className="row row-form section-row">
                {this.renderField({
                    label: "ObstetricalFile_PartnerSurnameLabel",
                    field: this.controlRenderer.renderTextbox({
                        value: partnerInfo.FullName,
                        onChange: text => this.props.mutateModelState(s => s.Section1.PartnerInformation.FullName = text),
                        maxLength: this.page1FieldLimits.PartnerName.MaxChars
                    }),
                    className: "section-column-1"
                })}
                {this.renderField({
                    label: "OccupationLabel",
                    field: this.controlRenderer.renderTextbox({
                        value: partnerInfo.Occupation,
                        onChange: text => this.props.mutateModelState(s => s.Section1.PartnerInformation.Occupation = text),
                        maxLength: this.page1FieldLimits.PartnerOccupation.MaxChars
                    }),
                    className: "section-column-1"
                })}
                <div className="section-column-1 flex-grow-1">
                    <div className="flex-display" style={{ justifyContent: "stretch", flexFlow: "wrap" }}>
                        {this.renderField({
                            label: "ObstetricalFile_BioFatherDonorFullNameLabel",
                            field: (
                                <div className="flex-display flex-align-items-center">
                                    {this.controlRenderer.renderCheckbox(
                                        "SameAsPartnerLabel",
                                        partnerInfo.IsBiologicialFatherSameAsPartner,
                                        value => this.props.mutateModelStateWithFormUpdate(s => s.Section1.PartnerInformation.IsBiologicialFatherSameAsPartner = value),
                                        "margin-sm-right"
                                    )}
                                    {this.controlRenderer.renderTextbox({
                                        value: partnerInfo.BiologicalFatherFullName,
                                        onChange: text => this.props.mutateModelState(s => s.Section1.PartnerInformation.BiologicalFatherFullName = text),
                                        className: "flex-grow-1",
                                        maxLength: this.page1FieldLimits.BiologicalFatherName.MaxChars
                                    })}
                                </div>
                            ),
                            className: "margin-sm-right"
                        })}
                        <div className="margin-sm-right" style={{ width: "50px", marginRight: "10px" }}>
                            {this.renderField({
                                label: "AgeLabel",
                                field: this.controlRenderer.renderTextbox({
                                    value: partnerInfo.BiologicalFatherAge?.toString() ?? "",
                                    isNumeric: true,
                                    onChange: value => this.props.mutateModelState(s => s.Section1.PartnerInformation.BiologicalFatherAge = value ? parseFloat(value) : null),
                                    maxLength: this.page1FieldLimits.BiologicalFatherAge.MaxChars
                                })
                            })}
                        </div>
                        {this.renderField({
                            label: "EthnicityLabel",
                            field: this.controlRenderer.renderTextbox({
                                value: partnerInfo.BiologicalFatherEthnicity,
                                onChange: text => this.props.mutateModelState(s => s.Section1.PartnerInformation.BiologicalFatherEthnicity = text),
                                maxLength: this.page1FieldLimits.BiologicalFatherEthnicity.MaxChars
                            })
                        })}
                    </div>
                </div>
            </div>
        );
    }

    private renderIndigenousIdentitySection(): JSX.Element {
        const identity = this.props.form.Section1.IndigenousIdentity;
        return (
            <div className="section-column-2 flex-display">
                <div className="flex-display flex-grow-1">
                    {this.renderField({
                        label: "IndigenousIdentityLabel",
                        field: (
                            <div style={{ marginTop: "7px" }}>
                                {this.controlRenderer.renderCheckbox(
                                    "IndigenousIdentity_NoResponseLabel",
                                    identity.NoResponse,
                                    value => this.props.mutateModelStateWithFormUpdate(s => s.Section1.IndigenousIdentity.NoResponse = value)
                                )}
                                {this.controlRenderer.renderCheckbox(
                                    "NoneWordLabel",
                                    identity.NoIndigenousIdentity,
                                    value => this.props.mutateModelStateWithFormUpdate(s => s.Section1.IndigenousIdentity.NoIndigenousIdentity = value)
                                )}
                            </div>
                        ),
                        className: "margin-xs-right"
                    })}
                    <div className="margin-xs-right">
                        {this.controlRenderer.renderCheckbox(
                            "IndigenousIdentity_FirstNationsLabel",
                            identity.IsFirstNations,
                            value => this.props.mutateModelStateWithFormUpdate(s => s.Section1.IndigenousIdentity.IsFirstNations = value)
                        )}
                        {this.controlRenderer.renderCheckbox(
                            "IndigenousIdentity_MetisLabel",
                            identity.IsMetis,
                            value => this.props.mutateModelStateWithFormUpdate(s => s.Section1.IndigenousIdentity.IsMetis = value)
                        )}
                        {this.controlRenderer.renderCheckbox(
                            "IndigenousIdentity_InukInuitLabel",
                            identity.IsInukInuit,
                            value => this.props.mutateModelStateWithFormUpdate(s => s.Section1.IndigenousIdentity.IsInukInuit = value)
                        )}
                        {this.controlRenderer.renderCheckbox(
                            "IndigenousIdentity_OutsideCanadaLabel",
                            identity.IsOutsideCanada,
                            value => this.props.mutateModelStateWithFormUpdate(s => s.Section1.IndigenousIdentity.IsOutsideCanada = value)
                        )}
                    </div>
                    <div style={{ padding: "0 5px", margin: "0 5px" }}>
                        {this.controlRenderer.renderCheckbox(
                            "StatusLabel",
                            identity.HasStatus === IndigenousIdentityStatus.Status,
                            value => this.props.mutateModelStateWithFormUpdate(s => s.Section1.IndigenousIdentity.HasStatus = value === true ? IndigenousIdentityStatus.Status : null)
                        )}
                        {this.controlRenderer.renderCheckbox(
                            "NonStatusLabel",
                            identity.HasStatus === IndigenousIdentityStatus.NonStatus,
                            value => this.props.mutateModelStateWithFormUpdate(s => s.Section1.IndigenousIdentity.HasStatus = value === true ? IndigenousIdentityStatus.NonStatus : null)
                        )}
                        {this.controlRenderer.renderCheckbox(
                            "PendingLabel",
                            identity.HasStatus === IndigenousIdentityStatus.Pending,
                            value => this.props.mutateModelStateWithFormUpdate(s => s.Section1.IndigenousIdentity.HasStatus = value === true ? IndigenousIdentityStatus.Pending : null)
                        )}
                    </div>
                    <div>
                        {this.controlRenderer.renderCheckbox(
                            "IndigenousIdentity_LiveOnReserverLabel",
                            identity.ReserveLivingStatus === ReserveLivingStatus.OnReserve,
                            value => this.props.mutateModelStateWithFormUpdate(s => s.Section1.IndigenousIdentity.ReserveLivingStatus = value ? ReserveLivingStatus.OnReserve : null)
                        )}
                        {this.controlRenderer.renderCheckbox(
                            "IndigenousIdentity_LiveOffReserverLabel",
                            identity.ReserveLivingStatus === ReserveLivingStatus.OffReserve,
                            value => this.props.mutateModelStateWithFormUpdate(s => s.Section1.IndigenousIdentity.ReserveLivingStatus = value ? ReserveLivingStatus.OffReserve : null)
                        )}
                        {this.controlRenderer.renderCheckbox(
                            "IndigenousIdentity_LiveOnAndOffReserverLabel",
                            identity.ReserveLivingStatus === ReserveLivingStatus.OnAndOffReserve,
                            value => this.props.mutateModelStateWithFormUpdate(s => s.Section1.IndigenousIdentity.ReserveLivingStatus = value ? ReserveLivingStatus.OnAndOffReserve : null)
                        )}
                    </div>
                    {this.renderField({
                        label: "EthnicityLabel",
                        field: this.controlRenderer.renderTextbox({
                            value: this.props.form.Section1.Ethnicity,
                            onChange: text => this.props.mutateModelState(s => s.Section1.Ethnicity = text),
                            maxLength: this.page1FieldLimits.Ethnicity.MaxChars
                        }),
                        className: "padding-md-left flex-grow-1"
                    })}
                </div>
            </div>
        );
    }

    private getEducationLevelOptions(): SearchableItemModelT<string>[] {
        return [
            new SearchableItemModelT(this.getString("EducationLevel_LessThanHighSchoolLabel"), "EducationLevel_LessThanHighSchoolLabel"),
            new SearchableItemModelT(this.getString("EducationLevel_HighSchoolDiploma"), "EducationLevel_HighSchoolDiploma"),
            new SearchableItemModelT(this.getString("EducationLevel_TradeOrOtherCertificateDiplomaLabel"), "EducationLevel_TradeOrOtherCertificateDiplomaLabel"),
            new SearchableItemModelT(this.getString("EducationLevel_UndergraduateUniversityDegreesLabel"), "EducationLevel_UndergraduateUniversityDegreesLabel"),
            new SearchableItemModelT(this.getString("EducationLevel_PostGraduateUniversityDegreesLabel"), "EducationLevel_PostGraduateUniversityDegreesLabel"),
            new SearchableItemModelT(this.getString("UnknownLabel"), "UnknownLabel")
        ];
    }
}

export { BritishColumbiaObstetricalFileSection1 };
